// ═══════════════════════════════════════════════════════════════════════════
// commandDispatch/systemCommands.ts — WBLOCK / PURGE / RENAME / PRINT /
// PAGESETUP / SCALELISTEDIT / BLOCKPROPERTIESMANAGER / LAYOUT RENAME /
// EATTEXT / BCOUNT / XREF-suite / BATTMAN / ATTSYNC, extracted verbatim
// from App.tsx's `submitCommandText` (D5).
// ═══════════════════════════════════════════════════════════════════════════

import {
  countBlockInstances,
  formatBlockCountSummary,
} from "@dxf-ai-editor/command-engine";

import { validateRenameSpace } from "../state/spaceMutationGuards";

import type { DispatchContext, DispatchResult } from "./types";

/**
 * ── WBLOCK / PURGE / RENAME (Phase 2.6 / 2.7 / 2.8) ──────────
 * These commands are stateless one-shot dialogs. They match both the
 * English names AutoCAD ships with and the AutoCAD CIS Russian aliases
 * (ЗПБЛОК / ОЧИСТИТЬ / ПЕРЕИМ). When recognised we open the corresponding
 * dialog and short-circuit the standard drafting flow. Also covers PRINT /
 * PAGESETUP / SCALELISTEDIT / BLOCKPROPERTIESMANAGER / the LAYOUT RENAME
 * regex sub-command / EATTEXT / BCOUNT / the XREF suite / BATTMAN / ATTSYNC
 * — all one-shot dialog-openers or reports that shared the same
 * `!draftSession` guard in the original inline router.
 */
export function handleSystemCommands(ctx: DispatchContext): DispatchResult {
  const {
    draftSession,
    resolvedCommandId,
    trimmedText,
    state,
    dispatch,
    geometryDocument,
    selectedEntities,
    appendCommandFeed,
    t,
    setCommandText,
    setWriteBlockDialogOpen,
    openPurgeDialog,
    setRenameBlockDialogOpen,
    setLayerManagerOpen,
    printDialog,
    pageSetupDialog,
    scaleListEditDialog,
    bpmDialog,
    eattextDialog,
    xrefDialog,
    battmanDialog,
  } = ctx;

  if (draftSession) return null;

  if (resolvedCommandId === "WBLOCK") {
    setWriteBlockDialogOpen(true);
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "PURGE") {
    openPurgeDialog();
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "RENAME") {
    setRenameBlockDialogOpen(true);
    setCommandText("");
    return true;
  }
  // T7 (agent-bridge layer-mgmt), local Codex round-7 P2 — LAYER (bare
  // alias LAYER/LA/СЛОЙ/СЛ) opens the Layer Manager. Same one-shot
  // dialog-opener pattern as PURGE/RENAME directly above — typed
  // NEW/RENAME arguments are NOT parsed here (that flow is the AI safe
  // contour + the dialog's own New/inline-rename controls; see the LAYER
  // descriptor's help text in system.ts).
  if (resolvedCommandId === "LAYER") {
    setLayerManagerOpen(true);
    setCommandText("");
    return true;
  }
  // B11 — PRINT / PLOT command. Opens the page-setup dialog;
  // while open, the print preview overlay paints a rectangle on
  // the canvas tracing the printable area. The hook owns the
  // settings reset + open feed + state.
  if (resolvedCommandId === "PRINT") {
    printDialog.open();
    setCommandText("");
    return true;
  }
  // PDF-6.9a — PAGESETUP / ПАРАМЛИСТ command. Opens the
  // per-layout Page Setup Manager. Mirrors the PRINT routing
  // pattern above; the hook owns the snapshot + draft state.
  // AutoCAD's CIS Russian build uses ПАРАМЛИСТ — we also accept
  // the shorter ПАРАМЛ for typing parity.
  if (resolvedCommandId === "PAGESETUP") {
    pageSetupDialog.open();
    setCommandText("");
    return true;
  }
  // PR-9 — SCALELISTEDIT command-line surface. The command itself
  // dispatches `system/open-scalelist-dialog` (audit-only); App.tsx
  // mounts the dialog via the hook. Same pattern as RENAME /
  // PURGE — the audit dispatch happens BEFORE we open the dialog
  // so the command-line transcript reflects the user intent.
  if (resolvedCommandId === "SCALELISTEDIT") {
    dispatch({
      type: "system/open-scalelist-dialog",
      source: "commandline",
    });
    scaleListEditDialog.open();
    setCommandText("");
    return true;
  }
  // ── BLOCKPROPERTIESMANAGER (Phase N.5) — drawing-wide editor ──
  // Works regardless of active space (no BEDIT requirement).
  // Aliases: BLOCKPROPERTIESMANAGER / BPM (en) / ДСБЛОК (ru).
  // Track 3 Phase 5 — owned by `useBpmDialog`.
  if (resolvedCommandId === "BLOCKPROPERTIESMANAGER") {
    bpmDialog.open();
    setCommandText("");
    return true;
  }
  // ── LAYOUT R / LAYOUT RENAME (B2) — rename a layout tab ──
  // AutoCAD parity: `LAYOUT R <old> <new>` or `LAYOUT RENAME <old> <new>`
  // renames a layout tab by its current display name. Russian
  // aliases: РАЗМЛИСТ (CIS command name), prefixes ПЕРЕИМ / П for
  // the sub-command. The name lookup is case-sensitive first, then
  // falls back to case-insensitive matching to stay forgiving —
  // AutoCAD itself accepts a case-insensitive match at the command
  // line.
  //
  // Guards:
  //   - space not found → warning, state unchanged
  //   - Model space     → cannot rename (AutoCAD parity)
  //   - duplicate name  → warning, state unchanged
  //   - empty name      → warning, state unchanged
  //
  // Malformed inputs (missing old or new name) fall through to the
  // shell routing so the user sees the standard LAYOUT usage
  // warning via the command-not-wired feedback path.
  {
    const layoutRenameMatch = trimmedText.match(
      /^\s*(?:-?LAYOUT|РАЗМЛИСТ)\s+(?:R|RENAME|ПЕРЕИМ|П)\s+(\S+)\s+(\S+)\s*$/iu,
    );
    if (layoutRenameMatch && layoutRenameMatch[1] && layoutRenameMatch[2]) {
      const oldName = layoutRenameMatch[1];
      const newName = layoutRenameMatch[2];
      const spaces = state.drawing.spaces;
      // Case-sensitive first, then case-insensitive fallback.
      const matchExact = spaces.find((s) => s.name === oldName);
      const matchCI =
        matchExact ??
        spaces.find((s) => s.name.toLowerCase() === oldName.toLowerCase());
      if (!matchCI) {
        appendCommandFeed(t("layout.rename.notfound", { name: oldName }));
        setCommandText("");
        return true;
      }
      // Guards live in the SHARED `validateRenameSpace`
      // (spaceMutationGuards.ts) — the same predicate both reducer
      // arms evaluate, so the command-line arm accepts/rejects
      // identically (Codex P2-2: the duplicate check is
      // case-INSENSITIVE like AutoCAD; Codex P2-4: >255-char names
      // are rejected before dispatch).
      const trimmedNew = newName.trim();
      const renameRejection = validateRenameSpace(spaces, matchCI.id, trimmedNew);
      if (renameRejection !== null) {
        appendCommandFeed(
          renameRejection === "model" || renameRejection === "not-layout"
            ? t("layout.rename.cannot_rename_model")
            : renameRejection === "not-found"
              ? t("layout.rename.notfound", { name: oldName })
              : renameRejection === "empty"
                ? t("layout.rename.empty_name")
                : renameRejection === "name-too-long"
                  ? t("layout.name.too_long")
                  : t("layout.rename.duplicate_name", {
                      name: trimmedNew,
                    }),
        );
        setCommandText("");
        return true;
      }
      dispatch({
        type: "shell/rename-space",
        source: "commandline",
        spaceId: matchCI.id,
        newName: trimmedNew,
      });
      appendCommandFeed(
        t("layout.rename.success", {
          old: matchCI.name,
          new: trimmedNew,
        }),
      );
      setCommandText("");
      return true;
    }
  }
  // ── EATTEXT (Phase FIN.5) — Attribute Extraction ──
  // Track 3 Phase 5 — owned by `useEattextDialog`.
  if (resolvedCommandId === "EATTEXT") {
    eattextDialog.open();
    setCommandText("");
    return true;
  }
  // ── BCOUNT (Phase FIN.6) — block instance count ──
  if (resolvedCommandId === "BCOUNT") {
    const entries = countBlockInstances({
      drawingDocument: geometryDocument,
    });
    const summary = formatBlockCountSummary(entries);
    appendCommandFeed(summary);
    setCommandText("");
    return true;
  }
  // ── XREF suite (Phase FIN.3) ───────────────────────────────
  // XREF / -XREF open the manager dialog. The other XREF commands
  // require a selected xref or open the manager so the user can
  // pick one from the dropdown — we route them all to the dialog.
  if (resolvedCommandId === "XREF") {
    xrefDialog.open();
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "XATTACH") {
    // Open dialog and trigger file picker as soon as it mounts (the
    // dialog auto-focuses; user clicks Attach explicitly).
    xrefDialog.open();
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "XBIND") {
    xrefDialog.open();
    setCommandText("");
    return true;
  }
  if (
    resolvedCommandId === "XUNLOAD" ||
    resolvedCommandId === "XRELOAD" ||
    resolvedCommandId === "XDETACH" ||
    resolvedCommandId === "XCLIP" ||
    resolvedCommandId === "XOPEN"
  ) {
    xrefDialog.open();
    setCommandText("");
    return true;
  }
  // ── BATTMAN (Phase 2.4) + ATTSYNC (Phase 2.5) ──────────────
  // BATTMAN opens the Block Attribute Manager. ATTSYNC is a
  // one-shot command: if an INSERT is currently selected we sync
  // that block; otherwise we fall back to the BATTMAN dialog so
  // the user can pick a block from the dropdown and press Sync.
  // Track 3 Phase 5 — BATTMAN routing owned by `useBattmanDialog`.
  if (resolvedCommandId === "BATTMAN") {
    battmanDialog.open();
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "ATTSYNC") {
    // If a single INSERT is selected, dispatch attsync directly.
    // codex-7.2 hunk #1: read the block name from the Properties
    // Palette dict (`properties.blockName` / `properties["Block name"]`)
    // because `EntityPreview` is not a discriminated union — the
    // typed-narrowing `insertSelection.blockName` access does not
    // type-check under stricter modes. We keep the established
    // `properties["Name"]` key as a final fallback to match the
    // surrounding ribbon-context-block pattern.
    const insertSelection = selectedEntities.find((e) => e.type === "Block");
    const selectedBlockName =
      insertSelection?.properties.blockName ??
      insertSelection?.properties["Block name"] ??
      insertSelection?.properties["Name"];
    if (insertSelection && selectedBlockName) {
      dispatch({
        type: "geometry/attsync",
        source: "commandline",
        blockName: selectedBlockName,
      });
      appendCommandFeed(t("feed.attsync", { block: selectedBlockName }));
    } else {
      // No selected INSERT — open BATTMAN so the user can pick a
      // block then press Sync (AutoCAD surfaces a command-line
      // prompt; the BATTMAN dialog covers the same flow and keeps
      // the UX single-window).
      battmanDialog.open();
    }
    setCommandText("");
    return true;
  }

  return null;
}
